/** @jsxImportSource ./simboluri-jsx */
// DETALIUL UNEI VERIFICĂRI, INLINE („vreau să pot da
// expand la verificare și să se deschidă în jos cu toate detaliile, să nu
// trebuiască să mă duc la pagina avansată").
//
// Se deschide sub rândul verificării și adună tot ce se știe despre ea:
//   * comparația concretă (valoare vs prag, cu R/E și rezerva),
//   * O PROPOZIȚIE din `catalog_verificari.py` (`scurt`) — ce se compară cu
//     ce. Restul explicației (formula, simbolurile, divergența de metodă,
//     soluțiile ordonate după cost) se deschide la cerere, cu „explicația
//     completă", și e aceeași care apare la hover pe titlu. Stefan,
//     „la validări e mult blabla […] teoria poate să fie doar
//     dacă dai hover pe titlu, că o știe deja structuristul";
//   * `detaliu`-ul brut al pasului: valorile intermediare pe care le pune
//     lângă verdict.
// Drumul spre pagina pasului rămâne, ca legătură — nu ca singura cale.

import { useState } from "react";
import { CategorieVerificare, CatalogVerificari, categoriaDin, tipDinNume } from "./tipuri";
import { VerificareElement, nr } from "./elemente";
import { ExplicatiaCompleta } from "./Verificare";
import { t as tm, useT } from "../i18n";
import type { Cheie } from "../i18n";

function fmt(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "number")
    // un număr întreg se scrie întreg: «29 de moduri», nu «29,00 moduri»
    return nr(
      v,
      Number.isInteger(v) ? 0 : Math.abs(v) < 10 ? 3 : Math.abs(v) < 100 ? 2 : 1
    );
  if (typeof v === "boolean") return tm(v ? "struct.det.da" : "struct.det.nu");
  if (Array.isArray(v)) return v.map(fmt).join(" · ");
  if (typeof v === "object") return "";
  return String(v);
}

// numele cheilor brute, ca să nu arate ca variabile de cod
// Notațiile de desen («N [kN]», «γ_Rd») rămân cum sunt: sunt simboluri, nu
// cuvinte. Restul trec prin catalog.
const CHEIE_SIMBOL: Record<string, string> = {
  N: "N [kN]", M2: "M₂ [kNm]", M3: "M₃ [kNm]", V2: "V₂ [kN]",
  RE: "R/E", nu_max: "ν_d", b_cm: "b [cm]", h_cm: "h [cm]",
  A_sw_cm2: "A_sw [cm²]", M_Rc_knm: "M_Rc [kNm]",
  sigma_s: "σ_s [MPa]", s_rmax_mm: "s_r,max [mm]",
  wk_mm: "w_k [mm]", zeta: "ζ", gamma_rd: "γ_Rd",
};

const CHEIE_ETICHETA: Record<string, Cheie> = {
  combo: "struct.brut.combo", loc: "struct.brut.loc",
  punct: "struct.brut.punct", nivel: "struct.brut.nivel",
  M_cap: "struct.brut.M_cap", H_liber_m: "struct.brut.H_liber_m",
  alpha_foaie: "struct.brut.alpha_foaie",
  indice_foaie: "struct.brut.indice_foaie",
  pas_cm: "struct.brut.pas_cm", rho_eff: "struct.brut.rho_eff",
  // cheile verdictelor globale — scrise ca întrebări, nu ca variabile
  masa_cumulata_x_pct: "struct.brut.masa_x",
  masa_cumulata_y_pct: "struct.brut.masa_y",
  masa_totala_t: "struct.brut.masa_totala",
  nr_moduri_extrase: "struct.brut.moduri_calculate",
  nr_moduri: "struct.brut.moduri_calculate",
  moduri_retinute_in_analiza: "struct.brut.moduri_folosite",
  nr_moduri_folosite: "struct.brut.moduri_folosite",
  nr_moduri_min_cerut: "struct.brut.moduri_min",
  masurat_pe: "struct.brut.masurat_pe",
  total_aplicat_kn: "struct.brut.total_aplicat",
  suma_reactiuni_kn: "struct.brut.suma_reactiuni",
  dezechilibru_kn: "struct.brut.dezechilibru_kn",
  dezechilibru_pct: "struct.brut.dezechilibru_pct",
  prag_pct: "struct.brut.prag_pct",
  valoare_pct: "struct.brut.valoare_pct",
  deficit_pct: "struct.brut.deficit_pct",
  v_baza_kn: "struct.brut.v_baza",
  f_b_echivalent_kn: "struct.brut.f_b_echivalent",
  raport_v_fb: "struct.brut.raport_v_fb",
  T_fundamental_s: "struct.brut.T_fundamental",
  caz: "struct.brut.caz",
};

export default function VerificareDetaliu({
  v,
  pas,
  catalog,
  linkPas,
  explicatie = true,
}: {
  v: VerificareElement;
  // cheia pasului care a emis verificarea (pentru catalogul de explicații)
  pas: string;
  catalog: CatalogVerificari | null;
  // linkul către pagina pasului, cu elementul selectat — rămâne ca legătură
  linkPas?: string;
  // `false` acolo unde explicația categoriei e deja pe ecran, o dată pentru
  // toate rândurile ei (cardul tipului din tabloul de verificări): rândul
  // rămâne cu CIFRELE lui, fără să repete formula de 130 de ori
  explicatie?: boolean;
}) {
  const t = useT();
  const tip = tipDinNume(catalog, pas, v.nume, v.grupa ?? undefined);
  const cat: CategorieVerificare | null = tip ? categoriaDin(catalog, tip) : null;
  // Unitatea se lipește DOAR când valoarea n-o poartă deja. Verdictele
  // globale își scriu unitatea în `E`/`R` de la construcție („91,1 %"), iar
  // adăugarea necondiționată dădea «91,1 % % față de ≥ 90,0 % %».
  const um = (x: unknown) => {
    const txt = String(x ?? "").trim();
    return v.unitate && txt && !txt.endsWith(v.unitate) ? ` ${v.unitate}` : "";
  };
  // câmpurile brute care merită arătate (fără sub-obiecte și fără ce e deja
  // în capul rândului)
  const brute = Object.entries(v.detaliu ?? {}).filter(
    ([k, val]) =>
      !["RE"].includes(k) &&
      val != null &&
      (typeof val !== "object" || Array.isArray(val)) &&
      fmt(val) !== ""
  );

  return (
    <div className="ob-ver-detaliu">
      {/* comparația concretă */}
      <div className="ob-ver-cmp">
        <span className="ob-lbl">{t("struct.det.comparatia")}</span>
        <span className="sg-mono">
          {v.E != null ? `${fmt(v.E)}${um(v.E)}` : "—"}
          {v.R != null
            ? t("struct.det.fata_de", { prag: `${fmt(v.R)}${um(v.R)}` })
            : ""}
          {v.RE != null ? t("struct.det.re", { re: nr(v.RE, 2) }) : ""}
          {v.RE != null && v.RE >= 1
            ? t("struct.det.rezerva", { n: nr((v.RE - 1) * 100) })
            : ""}
          {v.RE != null && v.RE < 1
            ? t("struct.det.lipsesc", { n: nr((1 - v.RE) * 100) })
            : ""}
        </span>
      </div>
      {v.acceptatMarja != null && (
        <div style={{ fontSize: 12 }}>
          <span className="ob-chip ob-c-amb" style={{ fontSize: 10 }}>
            {t("struct.det.marja_chip")}
          </span>{" "}
          <span style={{ color: "var(--mut)" }}>
            {t("struct.det.marja", { n: v.acceptatMarja })}
          </span>
        </div>
      )}
      {v.nota && (
        <div style={{ fontSize: 12, color: "var(--mut)" }}>{v.nota}</div>
      )}

      {/* explicația din catalog */}
      {explicatie ? (
        cat ? (
          <RezumatCategorie tip={tip ?? ""} cat={cat} referinta={v.referinta} />
        ) : (
          <div className="sg-muted" style={{ fontSize: 11 }}>
            {t("struct.det.fara_categorie")}
          </div>
        )
      ) : null}

      {/* valorile intermediare ale pasului */}
      {brute.length > 0 && (
        <div>
          <span className="ob-lbl" style={{ display: "block", marginBottom: 2 }}>
            {t("struct.det.valorile")}
          </span>
          <div className="ob-ver-brute">
            {brute.map(([k, val]) => (
              <div key={k}>
                <span style={{ color: "var(--mut)" }}>
                  {CHEIE_ETICHETA[k] ? t(CHEIE_ETICHETA[k]) : CHEIE_SIMBOL[k] ?? k}
                </span>{" "}
                <span className="sg-mono">{fmt(val)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {linkPas && (
        <a href={linkPas} style={{ fontSize: 11 }}>
          {t("struct.det.vezi_desenele")}
        </a>
      )}
    </div>
  );
}

/** Cuvântul scurt al costului: „gratuit — doar altă armătură…" → „gratuit". */
export function costScurt(cost: string): string {
  return String(cost || "").split(" — ")[0];
}

/** REZUMATUL unei categorii: propoziția `scurt`, articolul din care vine
 *  pragul, și atât. Teoria (ce verifică pe larg, formula, simbolurile,
 *  divergența, soluțiile) stă la hover pe titlu și sub butonul de aici — nu
 * în pagină. Când serverul e mai vechi decât și n-are `scurt`,
 *  cade pe prima frază din `ce`, ca rândul să nu rămână gol.
 *
 *  Soluțiile se arată în pagină NUMAI la cerere: la o verificare care trece,
 *  „ce se face dacă pică" e exact blabla-ul de care ne-am scăpat. */
export function RezumatCategorie({
  tip,
  cat,
  referinta,
}: {
  tip: string;
  cat: CategorieVerificare;
  referinta?: string | null;
}) {
  const t = useT();
  const [tot, setTot] = useState(false);
  const scurt = cat.scurt ?? primaFraza(cat.ce);
  return (
    <div className="ob-ver-rezumat">
      <div style={{ fontSize: 12 }}>{scurt}</div>
      <div className="ob-ver-rezumat-jos">
        {cat.articol && (
          <span className="ob-chip ob-c-mut" style={{ fontSize: 10 }}
                title={cat.sursa_formula_nume ?? cat.sursa_formula ?? undefined}>
            {cat.articol}
          </span>
        )}
        {referinta && (
          <span style={{ fontSize: 11, color: "var(--mut)" }}>{referinta}</span>
        )}
        {cat.divergenta && (
          <span className="ob-chip ob-c-met" style={{ fontSize: 10 }}
                title={t("struct.det.metoda_titlu")}>
            {t("struct.ver.marker_metoda")}
          </span>
        )}
        <button type="button" className="ob-ver-mai" onClick={() => setTot(!tot)}>
          {tot ? t("struct.det.ascunde") : t("struct.det.explicatia")}
        </button>
      </div>
      {tot && (
        <div className="ob-ver-teorie">
          <ExplicatiaCompleta tip={tip} cat={cat} />
        </div>
      )}
    </div>
  );
}

/** Prima frază a unui text — rezerva pentru un catalog fără `scurt`. */
function primaFraza(text: string): string {
  const t = String(text || "");
  const i = t.indexOf(". ");
  return i > 0 ? t.slice(0, i + 1) : t;
}

